// Shared Find & Replace dialog for Quill-based editors. Extracted from
// MailMerge.js's own (already working, already Word-styled) Find & Replace
// modal so the exact same feature can be reused by other surfaces (starting
// with SequenceBuilder.js, which had no Find & Replace at all — a confirmed
// gap from the earlier codebase audit) without duplicating the logic a
// second time. MailMerge.js's own usage is a pure refactor: same markup,
// same behavior, same visuals — just lifted into one file.
//
// Self-contained like WordRibbon.js's new Word Doc group: works off
// `getQuill()` + optional `addToast` alone, so it drops into any surface
// that already has those two things in scope.
import { useState } from "react";

export default function FindReplaceDialog({ open, onClose, getQuill, addToast, navTheme }) {
  const [findText, setFindText] = useState("");
  const [replaceText, setReplaceText] = useState("");
  const navColor = navTheme || "#0078d4";

  if (!open) return null;

  const doFind = () => {
    if (!findText) return;
    const q = getQuill?.();
    if (!q) return;
    const text = q.getText();
    const idx = text.toLowerCase().indexOf(findText.toLowerCase());
    if (idx >= 0) { q.setSelection(idx, findText.length); addToast?.(`Found "${findText}" at position ${idx}`, "success"); }
    else addToast?.(`"${findText}" not found`, "error");
  };

  const doReplace = () => {
    if (!findText) return;
    const q = getQuill?.();
    if (!q) return;
    const text = q.getText();
    const idx = text.toLowerCase().indexOf(findText.toLowerCase());
    if (idx >= 0) { q.deleteText(idx, findText.length); q.insertText(idx, replaceText); addToast?.(`Replaced "${findText}" with "${replaceText}"`, "success"); }
    else addToast?.(`"${findText}" not found`, "error");
  };

  const doReplaceAll = () => {
    if (!findText) return;
    const q = getQuill?.();
    if (!q) return;
    const text = q.getText();
    let count = 0;
    const regex = new RegExp(findText.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"), "gi");
    const matches = [...text.matchAll(regex)].reverse();
    matches.forEach(m => { q.deleteText(m.index, findText.length); q.insertText(m.index, replaceText); count++; });
    addToast?.(`Replaced ${count} occurrence(s)`, "success");
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.4)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center" }} onClick={onClose}>
      <div style={{ background: "#fff", borderRadius: 4, width: 420, boxShadow: "0 8px 32px rgba(0,0,0,.25)", fontFamily: "'Segoe UI',sans-serif", overflow: "hidden" }} onClick={e => e.stopPropagation()}>
        <div style={{ background: navColor, padding: "10px 16px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <p style={{ fontSize: 13, fontWeight: 600, color: "#fff" }}>Find and Replace</p>
          <button onClick={onClose} style={{ background: "none", border: "none", color: "#fff", fontSize: 18, cursor: "pointer" }}>&times;</button>
        </div>
        <div style={{ padding: 20 }}>
          <div style={{ marginBottom: 12 }}>
            <p style={{ fontSize: 12, fontWeight: 600, color: "#201f1e", marginBottom: 4 }}>Find:</p>
            <input autoFocus value={findText} onChange={e => setFindText(e.target.value)} placeholder="Text to find..."
              style={{ width: "100%", border: "1px solid #d1cec9", borderRadius: 3, padding: "7px 10px", fontSize: 13, outline: "none", boxSizing: "border-box" }}
              onKeyDown={e => e.key === "Enter" && doFind()} />
          </div>
          <div style={{ marginBottom: 16 }}>
            <p style={{ fontSize: 12, fontWeight: 600, color: "#201f1e", marginBottom: 4 }}>Replace with:</p>
            <input value={replaceText} onChange={e => setReplaceText(e.target.value)} placeholder="Replacement text..."
              style={{ width: "100%", border: "1px solid #d1cec9", borderRadius: 3, padding: "7px 10px", fontSize: 13, outline: "none", boxSizing: "border-box" }}
              onKeyDown={e => e.key === "Enter" && doReplace()} />
          </div>
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
            <button onClick={doFind} style={{ background: "#f3f2f1", border: "1px solid #d1cec9", borderRadius: 3, padding: "6px 14px", fontSize: 12, cursor: "pointer" }}>Find Next</button>
            <button onClick={doReplace} style={{ background: "#f3f2f1", border: "1px solid #d1cec9", borderRadius: 3, padding: "6px 14px", fontSize: 12, cursor: "pointer" }}>Replace</button>
            <button onClick={doReplaceAll} style={{ background: navColor, color: "#fff", border: "none", borderRadius: 3, padding: "6px 14px", fontSize: 12, cursor: "pointer", fontWeight: 600 }}>Replace All</button>
          </div>
        </div>
      </div>
    </div>
  );
}
